import { LoadingButton } from '@mui/lab';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { addDays, addYears, format, isValid, startOfDay } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  FILL_LATER_DATE_WINDOW_MESSAGE,
  FILL_LATER_LEAD_TIME_DAYS,
} from 'common/constants/fillLater';
import FillLaterAddressField from 'common/components/FillLaterAddressField';
import useRecipientAddresses from 'common/hooks/useRecipientAddresses';
import NewPatientAddressButton from 'modules/PatientView/components/ShippingTab/NewPatientAddressButton';
import PatientBanner from 'common/modules/PatientBanner';
import { moveLineItemsToFillLater } from 'store/thunks/patientThunks';

export const MAX_MOVE_ITEMS = 25;

const INVALID_FILL_DATE = 'invalid-date';

const asDateInput = (date) => format(date, 'yyyy-MM-dd');

const suggestedDate = () => asDateInput(addDays(startOfDay(new Date()), FILL_LATER_LEAD_TIME_DAYS));

// Each item ordered on its own date: the first is a lead time from today, and every one after
// it a lead time from the latest already picked, so two items are not created on the same day.
const nextSuggestedDate = (selection) => {
  const taken = Object.values(selection).filter(Boolean).sort();

  if (!taken.length) return suggestedDate();

  return asDateInput(
    addDays(new Date(`${taken[taken.length - 1]}T00:00:00`), FILL_LATER_LEAD_TIME_DAYS)
  );
};

const isAfterToday = (value) => {
  if (!value) return false;

  const picked = new Date(`${value}T00:00:00`);

  return isValid(picked) && picked > startOfDay(new Date());
};

const MoveToFillLaterDialog = ({
  open,
  onClose,
  onSuccess,
  mpi,
  orderId,
  orderDetails,
  lineItems,
  orderAddress,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [selection, setSelection] = useState({});
  const [addressByItem, setAddressByItem] = useState({});
  const [blockedItems, setBlockedItems] = useState({});
  const [verifiedItems, setVerifiedItems] = useState({});
  const {
    addresses,
    loading: addressesLoading,
    reload: reloadAddresses,
  } = useRecipientAddresses({
    orderId,
    enabled: open,
  });
  const storedAddressCount = useSelector(({ patient }) => patient?.[mpi]?.addresses?.length ?? 0);

  useEffect(() => {
    if (open && storedAddressCount) reloadAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedAddressCount]);

  const earliestSelectableDate = useMemo(() => addDays(startOfDay(new Date()), 1), []);

  const latestSelectableDate = useMemo(() => addYears(startOfDay(new Date()), 1), []);

  // minDate / maxDate only grey days out in the calendar; a date typed into the field reached
  // submit with nothing shown. This is what puts the reason on the field itself.
  const fillDateError = (value) => {
    if (!value || value === INVALID_FILL_DATE) return '';

    const chosen = startOfDay(new Date(`${value}T00:00:00`));
    if (!isValid(chosen)) return '';
    return chosen < earliestSelectableDate || chosen > latestSelectableDate
      ? FILL_LATER_DATE_WINDOW_MESSAGE
      : '';
  };

  const selectedIds = useMemo(
    () => Object.keys(selection).filter((lineItemId) => selection[lineItemId]),
    [selection]
  );

  const overCap = selectedIds.length > MAX_MOVE_ITEMS;

  const datesInvalid = selectedIds.filter((lineItemId) => !isAfterToday(selection[lineItemId]));

  const toggle = (lineItemId) =>
    setSelection((current) => ({
      ...current,
      [lineItemId]: current[lineItemId] ? undefined : nextSuggestedDate(current),
    }));

  const setDateFor = (lineItemId, value) =>
    setSelection((current) => ({ ...current, [lineItemId]: value }));

  const setAddressFor = (lineItemId, value) =>
    setAddressByItem((current) => ({ ...current, [lineItemId]: value }));

  const close = () => {
    setSelection({});
    setAddressByItem({});
    onClose();
  };

  const submit = () => {
    setIsLoading(true);

    const items = selectedIds.map((lineItemId) => ({
      orderId,
      lineItemId,
      scheduledFor: selection[lineItemId],
      ...(addressByItem[lineItemId] ? { addressDirectoryId: addressByItem[lineItemId] } : {}),
      ...(verifiedItems[lineItemId] ? { isManuallyVerified: true } : {}),
    }));

    return dispatch(moveLineItemsToFillLater({ mpi, items }))
      .then((moved) => {
        if (moved) {
          enqueueSnackbar(
            `${items.length} item${items.length === 1 ? '' : 's'} moved to fill later`,
            { variant: 'success' }
          );
          onSuccess?.();
          close();
        }
      })
      .finally(() => setIsLoading(false));
  };

  const addressBlocked = selectedIds.some((lineItemId) => blockedItems[lineItemId]);

  const canSubmit =
    selectedIds.length > 0 &&
    !overCap &&
    datesInvalid.length === 0 &&
    !isLoading &&
    !addressBlocked;

  return (
    <Dialog open={open} onClose={close} maxWidth='sm' fullWidth>
      <DialogTitle>Move items to Fill Later</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <PatientBanner mpi={mpi} />
        </Box>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            ['Order', orderId],
            ['Status', orderDetails.status],
            ['Source', orderDetails.source],
            ['Fulfilling Pharmacy', orderDetails.fulfillingPharmacy],
            ['Partner', orderDetails.partnerName],
            ['Practice', orderDetails.practiceName],
          ].map(([label, value]) => (
            <Grid item xs={6} sm={4} key={label}>
              <Typography variant='caption' color='text.secondary' display='block'>
                {label}
              </Typography>
              <Typography variant='body2' fontWeight={600} noWrap title={value || 'NA'}>
                {value || 'NA'}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {/* Nothing here ships today, which is the opposite of what a dialog full of items and
            dates suggests - so it is called out rather than set as quiet body copy. */}
        <Alert severity='warning' sx={{ mb: 2 }}>
          Nothing ships now. Each item you pick moves to a new order that stays on hold until the
          date you choose.
        </Alert>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 1,
          }}
        >
          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
            Pick the items to move
          </Typography>
          <Box
            sx={{
              flexShrink: 0,
              '& .MuiButton-root': {
                py: 0.3,
                px: 1.25,
                minWidth: 0,
                fontSize: '0.72rem',
                boxShadow: 'none',
              },
            }}
          >
            <NewPatientAddressButton mpi={mpi} showDeliveryInstructions={false}>
              Add a new address
            </NewPatientAddressButton>
          </Box>
        </Box>

        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            px: 1.5,
            py: 1.5,
            // Up to MAX_MOVE_ITEMS rows, each carrying a date and an address field: left to grow,
            // the list pushes the footer off the screen and the whole dialog scrolls, so the
            // buttons are only reachable at the bottom of a long list. The list scrolls instead.
            maxHeight: 360,
            overflowY: 'auto',
          }}
        >
          {lineItems.map((item) => {
            const checked = Boolean(selection[item.lineItemId]);

            return (
              <Box
                key={item.lineItemId}
                sx={{
                  py: 1,
                  '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' },
                }}
              >
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Tooltip
                    title={item.isMovable ? '' : item.blockedReason || ''}
                    placement='top'
                    arrow
                  >
                    {/* A disabled checkbox swallows pointer events, so the span is what the
                        tooltip listens on - and what a test hovers. */}
                    <span>
                      <Checkbox
                        checked={checked}
                        disabled={!item.isMovable}
                        onChange={() => toggle(item.lineItemId)}
                        inputProps={{ 'aria-label': `select ${item.drugName || item.lineItemId}` }}
                      />
                    </span>
                  </Tooltip>
                  <Box sx={{ flex: 1, minWidth: 0, opacity: item.isMovable ? 1 : 0.5 }}>
                    <Typography variant='body2' noWrap>
                      {item.drugName || item.lineItemId}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {`Rx ${item.rxNumber || 'N/A'} · qty ${item.quantity ?? '-'}`}
                      {item.isMovable || !item.blockedReason
                        ? ''
                        : ` · ${item.blockedReason.toLowerCase()}`}
                    </Typography>
                  </Box>
                  <DesktopDatePicker
                    value={
                      checked && selection[item.lineItemId] !== INVALID_FILL_DATE
                        ? new Date(`${selection[item.lineItemId]}T00:00:00`)
                        : null
                    }
                    onChange={(date) =>
                      setDateFor(
                        item.lineItemId,
                        isValid(date) ? asDateInput(date) : INVALID_FILL_DATE
                      )
                    }
                    disabled={!checked || !item.isMovable}
                    minDate={earliestSelectableDate}
                    maxDate={latestSelectableDate}
                    shouldDisableDate={(date) =>
                      startOfDay(date) < earliestSelectableDate ||
                      startOfDay(date) > latestSelectableDate
                    }
                    renderInput={(params) => (
                      <TextField
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...params}
                        size='small'
                        sx={{ width: 190 }}
                        error={checked && !!fillDateError(selection[item.lineItemId])}
                        helperText={checked ? fillDateError(selection[item.lineItemId]) : ''}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': `fill date for ${item.drugName || item.lineItemId}`,
                        }}
                      />
                    )}
                  />
                </Stack>

                {checked && item.isMovable && (
                  <Box sx={{ pl: 5.25, pt: 2 }}>
                    <FillLaterAddressField
                      id={`MoveToFillLater-address-${item.lineItemId}`}
                      addresses={addresses}
                      value={addressByItem[item.lineItemId] ?? null}
                      onChange={(value) => setAddressFor(item.lineItemId, value)}
                      onBlockedChange={(isBlocked) =>
                        setBlockedItems((current) => ({
                          ...current,
                          [item.lineItemId]: isBlocked,
                        }))
                      }
                      onAcknowledgedChange={(isAcknowledged) =>
                        setVerifiedItems((current) => ({
                          ...current,
                          [item.lineItemId]: isAcknowledged,
                        }))
                      }
                      inheritedAddress={orderAddress}
                      loading={addressesLoading}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {overCap && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {`A move is limited to ${MAX_MOVE_ITEMS} items. Deselect ${
              selectedIds.length - MAX_MOVE_ITEMS
            } to continue.`}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close} disabled={isLoading}>
          Cancel
        </Button>
        <LoadingButton
          variant='contained'
          loading={isLoading}
          disabled={!canSubmit}
          onClick={submit}
        >
          {`Move ${selectedIds.length || ''} to Fill Later`.replace('  ', ' ')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

MoveToFillLaterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  mpi: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
  orderDetails: PropTypes.shape({
    status: PropTypes.string,
    source: PropTypes.string,
    fulfillingPharmacy: PropTypes.string,
    partnerName: PropTypes.string,
    practiceName: PropTypes.string,
  }).isRequired,
  orderAddress: PropTypes.shape({}),
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      isMovable: PropTypes.bool,
      blockedReason: PropTypes.string,
      drugName: PropTypes.string,
      rxNumber: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
};

MoveToFillLaterDialog.defaultProps = {
  onSuccess: null,
  orderAddress: null,
};

export default MoveToFillLaterDialog;
